<template>
  <div class="path-tester">
    <h2>SVG Pfad Tester</h2>

    <!-- Pfad eingeben -->
    <div class="input-section">
      <label>SVG Pfad eingeben:</label>
      <input
        v-model="testPath"
        placeholder="z.B. /assets/sinechart_export.svg"
        class="path-input"
      />
      <button @click="testCurrentPath" class="btn btn-test">Pfad testen</button>
    </div>

    <!-- Vordefinierte Pfade testen -->
    <div class="preset-paths">
      <h3>Häufige Pfade testen:</h3>
      <div class="path-buttons">
        <button
          v-for="path in commonPaths"
          :key="path"
          @click="testSpecificPath(path)"
          class="btn btn-preset"
        >
          {{ path }}
        </button>
      </div>
    </div>

    <!-- Test Ergebnisse -->
    <div class="results">
      <div v-for="result in testResults" :key="result.path" class="result-item">
        <div class="result-header">
          <span class="path">{{ result.path }}</span>
          <span :class="['status', result.success ? 'success' : 'error']">
            {{ result.success ? '✅ Funktioniert' : '❌ Fehler' }}
          </span>
        </div>
        <div v-if="!result.success" class="error-details">
          {{ result.error }}
        </div>
        <div v-if="result.success && result.content" class="content-preview">
          <details>
            <summary>SVG Inhalt anzeigen</summary>
            <pre>{{ result.content.substring(0, 500) }}{{ result.content.length > 500 ? '...' : '' }}</pre>
          </details>
        </div>
      </div>
    </div>

    <!-- Datei-Upload als Alternative -->
    <div class="upload-section">
      <h3>Oder SVG-Datei direkt hochladen:</h3>
      <input
        type="file"
        accept=".svg"
        @change="handleFileUpload"
        class="file-input"
      />
    </div>

    <!-- SVG Vorschau -->
    <div v-if="svgPreview" class="svg-preview">
      <h3>SVG Vorschau:</h3>
      <div class="preview-container" v-html="svgPreview"></div>
      <button @click="useThisSvg" class="btn btn-use">
        Diese SVG für Vivus verwenden
      </button>
    </div>

    <!-- Vivus Demo -->
    <div v-if="selectedSvg" class="vivus-demo">
      <h3>Vivus Animation:</h3>
      <div ref="vivusContainer" class="vivus-container"></div>
      <div class="vivus-controls">
        <button @click="startVivusAnimation" class="btn btn-animate">
          Animation starten
        </button>
        <button @click="resetVivusAnimation" class="btn btn-reset">
          Reset
        </button>
      </div>
    </div>

    <!-- Hilfe -->
    <div class="help-section">
      <h3>💡 Tipps zur Pfad-Findung:</h3>
      <ul>
        <li><strong>Public Ordner:</strong> Dateien in <code>public/</code> sind über <code>/dateiname</code> erreichbar</li>
        <li><strong>Unterordner:</strong> <code>public/assets/bild.svg</code> → <code>/assets/bild.svg</code></li>
        <li><strong>Development Server:</strong> Meist läuft auf <code>http://localhost:3000</code></li>
        <li><strong>Browser-Test:</strong> Öffne <code>http://localhost:3000/dein/pfad.svg</code> direkt</li>
        <li><strong>Netzwerk-Tab:</strong> Öffne F12 → Network und schaue ob die Datei geladen wird</li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onUnmounted } from 'vue'

interface TestResult {
  path: string
  success: boolean
  error?: string
  content?: string
}

const testPath = ref('./assets/sinechart_export.svg')
const testResults = ref<TestResult[]>([])
const svgPreview = ref('')
const selectedSvg = ref('')
const vivusContainer = ref<HTMLDivElement>()

// Häufige Pfad-Varianten
const commonPaths = [
  '/assets/sinechart_export.svg',
  '/src/assets/sinechart_export.svg',
  '/public/assets/sinechart_export.svg',
  './assets/sinechart_export.svg',
  '../assets/sinechart_export.svg',
  '/sinechart_export.svg',
  '/static/sinechart_export.svg',
  '/images/sinechart_export.svg'
]

let vivusInstance: any = null

onUnmounted(() => {
  vivusInstance?.destroy()
})

const testSpecificPath = async (path: string) => {
  testPath.value = path
  await testCurrentPath()
}

const testCurrentPath = async () => {
  const path = testPath.value.trim()
  if (!path) return

  // Vorherige Ergebnisse für diesen Pfad entfernen
  testResults.value = testResults.value.filter(r => r.path !== path)

  try {
    console.log(`Teste Pfad: ${path}`)

    const response = await fetch(path)

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const content = await response.text()

    if (!content.includes('<svg')) {
      throw new Error('Datei enthält kein <svg> Element')
    }

    testResults.value.unshift({
      path,
      success: true,
      content
    })

    // Automatisch als Vorschau setzen wenn erfolgreich
    svgPreview.value = content

  } catch (error: any) {
    console.error(`Fehler bei Pfad ${path}:`, error)

    testResults.value.unshift({
      path,
      success: false,
      error: error.message
    })
  }
}

const handleFileUpload = (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (!file) return

  if (!file.type.includes('svg')) {
    alert('Bitte wähle eine SVG-Datei aus')
    return
  }

  const reader = new FileReader()
  reader.onload = (e) => {
    const content = e.target?.result as string
    svgPreview.value = content

    // Zu Testergebnissen hinzufügen
    testResults.value.unshift({
      path: `📁 ${file.name}`,
      success: true,
      content
    })
  }
  reader.readAsText(file)
}

const useThisSvg = () => {
  selectedSvg.value = svgPreview.value

  // SVG in Vivus Container einfügen
  if (vivusContainer.value) {
    vivusContainer.value.innerHTML = selectedSvg.value

    setTimeout(() => {
      initializeVivus()
    }, 100)
  }
}

const initializeVivus = async () => {
  try {
    // Dynamically import Vivus
    const { default: Vivus } = await import('vivus')

    const svgElement = vivusContainer.value?.querySelector('svg')
    if (!svgElement) {
      throw new Error('Kein SVG Element gefunden')
    }

    // SVG für Animation vorbereiten
    prepareSvgForVivus(svgElement)

    vivusInstance = new Vivus(svgElement, {
      type: 'delayed',
      duration: 200,
      start: 'manual',
      animTimingFunction: Vivus.EASE_OUT
    })

    console.log('Vivus erfolgreich initialisiert!')

  } catch (error) {
    console.error('Vivus Initialisierung fehlgeschlagen:', error)
    alert('Vivus konnte nicht initialisiert werden: ' + error.message)
  }
}

const prepareSvgForVivus = (svgElement: SVGElement) => {
  const elements = svgElement.querySelectorAll('*')

  elements.forEach((element: any) => {
    // Fills zu Strokes konvertieren
    if (element.getAttribute('fill') && element.getAttribute('fill') !== 'none') {
      element.setAttribute('stroke', element.getAttribute('fill'))
      element.setAttribute('fill', 'none')
    }

    // Default stroke für animierbare Elemente
    const animatableTags = ['path', 'line', 'polyline', 'circle', 'ellipse', 'rect', 'polygon']
    if (animatableTags.includes(element.tagName.toLowerCase())) {
      if (!element.getAttribute('stroke')) {
        element.setAttribute('stroke', '#000000')
        element.setAttribute('fill', 'none')
      }
      if (!element.getAttribute('stroke-width')) {
        element.setAttribute('stroke-width', '2')
      }
    }
  })
}

const startVivusAnimation = () => {
  vivusInstance?.reset().play()
}

const resetVivusAnimation = () => {
  vivusInstance?.reset()
}

// Beim Laden automatisch ersten Pfad testen
testSpecificPath(commonPaths[0])
</script>

<style scoped>
.path-tester {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
}

.input-section {
  margin: 20px 0;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.path-input {
  flex: 1;
  min-width: 300px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: monospace;
}

.preset-paths {
  margin: 30px 0;
}

.path-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 15px 0;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.btn-test {
  background: #3498db;
  color: white;
}

.btn-preset {
  background: #95a5a6;
  color: white;
  font-family: monospace;
  font-size: 12px;
}

.btn-use {
  background: #27ae60;
  color: white;
}

.btn-animate {
  background: #e67e22;
  color: white;
}

.btn-reset {
  background: #95a5a6;
  color: white;
}

.btn:hover {
  opacity: 0.8;
}

.results {
  margin: 30px 0;
}

.result-item {
  margin: 15px 0;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #f9f9f9;
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.path {
  font-family: monospace;
  background: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: bold;
}

.status.success {
  color: #27ae60;
  font-weight: bold;
}

.status.error {
  color: #e74c3c;
  font-weight: bold;
}

.error-details {
  color: #e74c3c;
  background: #ffeaea;
  padding: 10px;
  border-radius: 4px;
  font-size: 14px;
}

.content-preview {
  margin-top: 10px;
}

.content-preview pre {
  background: #fff;
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
  max-height: 200px;
  overflow-y: auto;
}

.upload-section {
  margin: 30px 0;
  padding: 20px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  text-align: center;
}

.file-input {
  margin: 10px 0;
}

.svg-preview {
  margin: 30px 0;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
}

.preview-container {
  border: 1px solid #ccc;
  padding: 20px;
  margin: 15px 0;
  background: #fafafa;
  text-align: center;
}

.preview-container svg {
  max-width: 100%;
  height: auto;
}

.vivus-demo {
  margin: 30px 0;
  padding: 20px;
  border: 1px solid #27ae60;
  border-radius: 8px;
  background: #f0fff0;
}

.vivus-container {
  border: 1px solid #ccc;
  padding: 20px;
  margin: 15px 0;
  background: white;
  text-align: center;
  min-height: 200px;
}

.vivus-controls {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.help-section {
  margin: 40px 0;
  padding: 20px;
  background: #e8f4f8;
  border-radius: 8px;
}

.help-section ul {
  margin: 15px 0;
}

.help-section li {
  margin: 8px 0;
}

.help-section code {
  background: #fff;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: monospace;
}

h2, h3 {
  color: #2c3e50;
}
</style>